import React from 'react';
import PropTypes from 'prop-types';
import Slide from '@material-ui/core/Slide';
import {
  SnackbarContentWrapper,
  EmojiIconMessageContainer,
  LinearProgressContainer,
  IconContainer,
  ActionRowContainer,
  EmojiContainer,
  SnackbarMUIContainer,
  ActionButton,
} from './Snackbar.styled';

const snackbarContentClasses = (isLightVariant) => ({
  root: `snackbarContentRoot ${
    isLightVariant ? 'snackbarContentRootLight' : 'snackbarContentRootDark'
  }`,
  message: 'snackbarContentClasses',
});

export const STANDARD_BUTTONS = {
  dismiss: {
    label: 'Dismiss',
    ariaLabel: 'dismiss',
    key: 'close',
    onClick: () => undefined,
  },
  retry: {
    label: 'Retry',
    ariaLabel: 'retry',
    key: 'retry',
    onClick: () => undefined,
  },
};

// isLightVariant is typically used only for Snackbars that relate to a progress bar, such as uploading, error uploading and upload successful

const Snackbar = ({
  open,
  icon,
  progress,
  isLightVariant,
  actionButtons,
  onClose,
  emoji,
  message,
  autoHideDuration,
  children,
  className,
  'data-testid': dataTestId,
}) => {
  const actionButtonsElements = (
    actionButtons || [{ ...STANDARD_BUTTONS.dismiss, onClick: onClose }]
  ).map(({ ariaLabel, key, label, onClick }) => (
    <ActionButton
      key={key}
      aria-label={ariaLabel || key}
      onClick={onClick}
      type="text"
      size="small"
    >
      <span>{label}</span>
    </ActionButton>
  ));

  return (
    <SnackbarMUIContainer
      open={open}
      onClose={onClose}
      TransitionComponent={Slide}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      autoHideDuration={autoHideDuration}
      className={className}
      data-testid={dataTestId}
    >
      <SnackbarContentWrapper
        classes={snackbarContentClasses(isLightVariant)}
        message={
          children || (
            <span>
              <EmojiIconMessageContainer>
                {emoji && <EmojiContainer>{emoji}</EmojiContainer>}
                {icon && <IconContainer>{icon}</IconContainer>}
                {message && <span>{message}</span>}
              </EmojiIconMessageContainer>
              {!!actionButtonsElements.length && (
                <ActionRowContainer>{actionButtonsElements}</ActionRowContainer>
              )}
              {progress !== null && (
                <LinearProgressContainer
                  variant="determinate"
                  value={progress}
                />
              )}
            </span>
          )
        }
      />
    </SnackbarMUIContainer>
  );
};

Snackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  emoji: PropTypes.string,
  icon: PropTypes.node,
  isLightVariant: PropTypes.bool,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  autoHideDuration: PropTypes.number,
  actionButtons: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func.isRequired,
      label: PropTypes.string.isRequired,
      ariaLabel: PropTypes.string,
    })
  ),
  progress: PropTypes.number,
  children: PropTypes.element,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
};

Snackbar.defaultProps = {
  emoji: null,
  onClose: null,
  icon: null,
  isLightVariant: false,
  message: null,
  autoHideDuration: 5000,
  actionButtons: null,
  progress: null,
  children: null,
  className: null,
  'data-testid': null,
};

export default Snackbar;
